import { MAX_EXT_ATTRIBUTES, EventTypes } from '../utils/consts.mjs';
import { isShortISODateString } from '../utils/date-checker.mjs';
import { isValidEmail } from '../utils/email-validator.mjs';
import { BaseEvent } from './base-event.mjs';
import { flattenObject } from '../utils/flatten-object.mjs';

// © Sitecore Corporation A/S. All rights reserved. Sitecore® is a registered trademark of Sitecore Corporation A/S.
class IdentityEvent extends BaseEvent {
    /**
     * A class that extends from {@link BaseEvent} and has all the required functionality to send a VIEW event
     * @param args - Unified object containing the required properties
     */
    constructor(args) {
        const { channel, currency, pointOfSale, language, page } = args.eventData;
        super({ channel, currency, language, page, pointOfSale }, args.settings, args.id, args.infer);
        this.extensionData = {};
        this.numberOfExtensionDataProperties = 0;
        this.validateAttributes(args.eventData);
        this.eventData = args.eventData;
        this.eventApiClient = args.eventApiClient;
        if (args.extensionData)
            this.extensionData = flattenObject({ object: args.extensionData });
        this.numberOfExtensionDataProperties = Object.entries(this.extensionData).length;
        if (this.numberOfExtensionDataProperties > MAX_EXT_ATTRIBUTES)
            throw new Error(`[IV-0005] This event supports maximum ${MAX_EXT_ATTRIBUTES} attributes. Reduce the number of attributes.`);
    }
    /**
     * Function that validates the identifiers object, email and date attributes for CDN users
     *  * @param eventData - The data to be validated
     */
    validateAttributes(eventData) {
        if (eventData.identifiers.length === 0)
            throw new Error(`[MV-0004] "identifiers" is required.`);
        if (eventData.dob !== undefined && !isShortISODateString(eventData.dob))
            throw new Error(`[IV-0002] Incorrect value for "dob". Format the value according to ISO 8601.`);
        eventData.identifiers.forEach((identifier) => {
            if (identifier.expiryDate && !isShortISODateString(identifier.expiryDate))
                throw new Error(`[IV-0004] Incorrect value for "expiryDate". Format the value according to ISO 8601.`);
        });
        if (eventData.email && !isValidEmail(eventData.email))
            throw new Error(`[IV-0003] Incorrect value for "email". Set the value to a valid email address.`);
    }
    /**
     * A function that maps the identity event input data with the payload sent to the API
     * @returns - The payload object
     */
    mapAttributes() {
        const identityPayload = {
            city: this.eventData.city,
            country: this.eventData.country,
            dob: this.eventData.dob,
            email: this.eventData.email,
            firstname: this.eventData.firstName,
            gender: this.eventData.gender,
            identifiers: this.eventData.identifiers.map((value) => {
                return {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    expiry_date: value.expiryDate,
                    id: value.id,
                    provider: value.provider,
                };
            }),
            lastname: this.eventData.lastName,
            mobile: this.eventData.mobile,
            phone: this.eventData.phone,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            postal_code: this.eventData.postalCode,
            state: this.eventData.state,
            street: this.eventData.street,
            title: this.eventData.title,
            type: EventTypes.Identity,
        };
        if (this.numberOfExtensionDataProperties > 0)
            identityPayload.ext = this.extensionData;
        return identityPayload;
    }
    /**
     * Sends the event to Sitecore CDP
     * @returns - A promise that resolves with either the Sitecore CDP response object or null
     */
    async send() {
        const baseAttr = this.mapBaseEventPayload();
        const eventAttrs = this.mapAttributes();
        const fetchBody = Object.assign({}, eventAttrs, baseAttr);
        return await this.eventApiClient.send(fetchBody);
    }
}

export { IdentityEvent };
